import React from 'react';
import clsx from 'clsx';
import { Icon } from '@common/components';
import {
  Colors,
  ColorsType,
  Duration,
  Easing,
  FontSize,
  FontWeight,
  LineHeight,
  Opacity,
  Radius,
  Spacing,
} from '@common/tokens';
import { getChipBorderColor, getReadableTextColor } from './_tag-service';

type TagProps = {
  children: React.ReactNode;
  color: ColorsType;
  textColor?: ColorsType;
  kind?: 'pill' | 'square' | 'chip';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  ariaLabel?: string;
  showX?: boolean;
};

export const Tag = ({
  children,
  color,
  textColor = getReadableTextColor(color),
  kind = 'pill',
  size = 'md',
  onClick,
  ariaLabel,
  showX = false,
  ...props
}: TagProps) => {
  const className = clsx(
    'wk-tag',
    `wk-tag--${kind}`,
    `wk-tag--size-${size}`,
    onClick && 'wk-tag--clickable',
    showX && 'wk-tag--with-x'
  );
  const content = (
    <>
      {children}
      {showX && (
        <span className="wk-tag__x">
          <Icon icon="xMark" kind="light" size="sm" />
        </span>
      )}
    </>
  );

  return (
    <>
      {/* NOTE: styled-jsx scopes only lowercase DOM elements, so a computed
          <Element> instead of these two branches would lose every .wk-tag style. */}
      {onClick ? (
        <button
          type="button"
          className={className}
          onClick={onClick}
          aria-label={ariaLabel}
          {...props}
        >
          {content}
        </button>
      ) : (
        <span className={className} aria-label={ariaLabel} {...props}>
          {content}
        </span>
      )}
      <style jsx>{`
        .wk-tag {
          display: inline-block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: middle;
          white-space: nowrap;
          color: ${Colors[textColor]};
          background-color: ${Colors[color]};
          line-height: ${LineHeight.sm};
          border: 1px solid transparent;
        }
        /* Kinds own shape (radius, border, weight); sizes own font-size and
           padding. Keep the axes apart: a kind must never set font/padding. */
        .wk-tag--pill {
          border-radius: ${Radius.lg};
          font-weight: ${FontWeight.bold};
        }
        .wk-tag--square {
          border-radius: ${Radius.sm};
          font-weight: ${FontWeight.medium};
        }
        .wk-tag--chip {
          border-radius: ${Radius.xl};
          border-color: ${getChipBorderColor(color)};
          font-weight: ${FontWeight.regular};
        }
        .wk-tag--size-sm {
          font-size: ${FontSize.xs};
          padding: 0 6px;
        }
        .wk-tag--size-md {
          font-size: ${FontSize.xs};
          padding: ${Spacing.xxs}px ${Spacing.sm}px;
        }
        .wk-tag--size-lg {
          font-size: ${FontSize.sm};
          padding: ${Spacing.xxs}px ${Spacing.sm}px;
        }
        .wk-tag--with-x {
          display: inline-flex;
          align-items: center;
          gap: ${Spacing.xs}px;
        }
        .wk-tag--clickable {
          cursor: pointer;
          font-family: inherit;
          transition: opacity ${Duration.fast} ${Easing.easeInOut};
        }
        .wk-tag--clickable:hover {
          opacity: ${Opacity.high};
        }
        /* outline, not box-shadow: shadows aren't painted under
           forced-colors (Windows High Contrast), which would leave keyboard
           users with no focus indicator. */
        .wk-tag--clickable:focus-visible {
          outline: 2px solid ${Colors.elm};
          outline-offset: 2px;
        }
      `}</style>
    </>
  );
};
